import React, { Component } from "react";
import {
  Plus, Trash2, Link as LinkIcon, Github, Gitlab, Facebook, Twitter,
  Linkedin, Instagram, Youtube, Dribbble, PenTool, Code, MessageSquare,
  Globe, ChevronDown, BookOpen, FileText, ExternalLink,
} from "lucide-react";
import BehanceIcon from "./icons/BehanceIcon";

import {
  Input,
  Button,
  IconButton,
  Tooltip,
  DropdownMenu,
} from "../../design-system";
import { cn } from "../../design-system/utils/cn";

const ICON_OPTIONS = [
  { type: "source",        icon: FileText,        label: "Source" },
  { type: "documentation", icon: BookOpen,        label: "Documentation" },
  { type: "demo",          icon: ExternalLink,    label: "Démo" },
  { type: "link",          icon: LinkIcon,        label: "Lien générique" },
  { type: "github",        icon: Github,          label: "GitHub" },
  { type: "gitlab",        icon: Gitlab,          label: "GitLab" },
  { type: "facebook",      icon: Facebook,        label: "Facebook" },
  { type: "twitter",       icon: Twitter,         label: "Twitter" },
  { type: "linkedin",      icon: Linkedin,        label: "LinkedIn" },
  { type: "instagram",     icon: Instagram,       label: "Instagram" },
  { type: "youtube",       icon: Youtube,         label: "YouTube" },
  { type: "dribbble",      icon: Dribbble,        label: "Dribbble" },
  { type: "behance",       icon: BehanceIcon,     label: "Behance" },
  { type: "medium",        icon: PenTool,         label: "Medium" },
  { type: "devto",         icon: Code,            label: "Dev.to" },
  { type: "codepen",       icon: Code,            label: "CodePen" },
  { type: "stackoverflow", icon: MessageSquare,   label: "Stack Overflow" },
  { type: "reddit",        icon: MessageSquare,   label: "Reddit" },
  { type: "discord",       icon: MessageSquare,   label: "Discord" },
  { type: "telegram",      icon: MessageSquare,   label: "Telegram" },
  { type: "whatsapp",      icon: MessageSquare,   label: "WhatsApp" },
  { type: "mastodon",      icon: MessageSquare,   label: "Mastodon" },
  { type: "website",       icon: Globe,           label: "Site web" },
];

function getOption(type) {
  return ICON_OPTIONS.find((o) => o.type === type) || ICON_OPTIONS[0];
}

/** Sélecteur d'icône social — DropdownMenu DS avec grille d'icônes. */
function IconPicker({ value, onChange }) {
  const current = getOption(value);
  const CurrentIcon = current.icon;

  return (
    <DropdownMenu
      side="bottom"
      align="start"
      trigger={
        <button
          type="button"
          title={current.label}
          aria-label={`Type : ${current.label}`}
          className="inline-flex items-center gap-1 h-10 px-2.5 rounded-md bg-surface border border-border hover:border-ink/40 transition-colors text-text"
        >
          <CurrentIcon className="w-4 h-4" strokeWidth={1.8} />
          <ChevronDown className="w-3 h-3 text-mute" strokeWidth={2} />
        </button>
      }
      className="!min-w-[240px] !p-2"
    >
      {({ close }) => (
        <>
          <div className="text-[10.5px] font-mono uppercase tracking-wider text-mute mb-1.5 px-1">
            Type de lien
          </div>
          <div className="grid grid-cols-4 gap-1">
            {ICON_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const isSelected = opt.type === value;
              return (
                <button
                  key={opt.type}
                  type="button"
                  onClick={() => { onChange(opt.type); close(); }}
                  title={opt.label}
                  aria-label={opt.label}
                  className={cn(
                    'aspect-square rounded-md inline-flex flex-col items-center justify-center gap-0.5 transition-colors',
                    isSelected
                      ? 'bg-ink text-cream'
                      : 'text-mute hover:text-text hover:bg-bg-2',
                  )}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.8} />
                </button>
              );
            })}
          </div>
        </>
      )}
    </DropdownMenu>
  );
}

class SocialLinksInput extends Component {
  constructor(props) {
    super(props);
    this.state = { links: props.value || [] };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({ links: this.props.value || [] });
    }
  }

  addLink = () => {
    const links = [...this.state.links, { type: "source", label: "", url: "", description: "" }];
    this.setState({ links });
    this.props.onChange(links);
  };

  removeLink = (index) => {
    const links = this.state.links.filter((_, i) => i !== index);
    this.setState({ links });
    this.props.onChange(links);
  };

  updateLink = (index, field, value) => {
    const links = [...this.state.links];
    links[index] = { ...links[index], [field]: value };
    this.setState({ links });
    this.props.onChange(links);
  };

  render() {
    const { links } = this.state;

    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-text">
            <LinkIcon className="w-4 h-4 text-mute" strokeWidth={1.8} />
            Liens et sources
          </div>
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Plus className="w-4 h-4" strokeWidth={1.8} />}
            onClick={this.addLink}
          >
            Ajouter un lien
          </Button>
        </div>

        {links.length === 0 ? (
          <div className="rounded-md border border-dashed border-border px-4 py-6 text-center">
            <p className="text-sm text-mute italic">
              Aucun lien ajouté. Clique sur « Ajouter un lien » pour commencer.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {links.map((link, index) => (
              <li
                key={index}
                className="flex items-start gap-2 p-2.5 rounded-md bg-bg-2/40 border border-border"
              >
                <IconPicker
                  value={link.type || "link"}
                  onChange={(t) => this.updateLink(index, "type", t)}
                />

                <div className="flex-1 min-w-0 space-y-2">
                  <Input
                    value={link.label || ""}
                    onChange={(e) => this.updateLink(index, "label", e.target.value)}
                    placeholder="Titre affiché, ex : Documentation API"
                    aria-label={`Titre du lien ${index + 1}`}
                  />
                  <Input
                    type="url"
                    value={link.url || ""}
                    onChange={(e) => this.updateLink(index, "url", e.target.value)}
                    placeholder="https://…"
                    aria-label={`URL du lien ${index + 1}`}
                  />
                  <Input
                    value={link.description || ""}
                    onChange={(e) => this.updateLink(index, "description", e.target.value)}
                    placeholder="Note courte, ex : référence utilisée dans l'article"
                    aria-label={`Description du lien ${index + 1}`}
                  />
                </div>

                <Tooltip content="Supprimer ce lien">
                  <IconButton
                    variant="ghost"
                    icon={<Trash2 strokeWidth={1.8} />}
                    aria-label="Supprimer ce lien"
                    onClick={() => this.removeLink(index)}
                  />
                </Tooltip>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
}

export default SocialLinksInput;
